import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Check, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

const CONTACT_TABLE_ID = 20074;
const ADMIN_TABLE_ID = 20097;

export default function ContactPage() {
  const location = useLocation();
  const { toast } = useToast();
  const [formType, setFormType] = useState<"demo" | "contact">("demo");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [industry, setIndustry] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Set page title
    document.title = "Contact Us | Book a Demo | AI Automation Co.";

    // Check URL params for any pre-selected form type
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get("type");
    if (typeParam === "demo" || typeParam === "contact") {
      setFormType(typeParam);
    }

    // Check if industry is pre-selected
    const industryParam = searchParams.get("industry");
    if (industryParam) {
      setIndustry(industryParam);
      setMessage(`I'm interested in AI automation solutions for my ${industryParam} business.`);
    }
  }, [location]);

  // Function to get admin emails from database
  const getAdminEmails = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(ADMIN_TABLE_ID, {
        "PageNo": 1,
        "PageSize": 100,
        "OrderByField": "ID",
        "IsAsc": false,
        "Filters": [
        {
          "name": "is_active",
          "op": "Equal",
          "value": true
        }]

      });

      if (error) {
        console.error('Error fetching admin emails:', error);
        return ['javedahmedshaikh@gmail.com']; // Fallback email
      }

      if (data && data.List && data.List.length > 0) {
        return data.List.map((admin) => admin.email).filter((email) => email);
      }

      return ['javedahmedshaikh@gmail.com']; // Fallback email
    } catch (error) {
      console.error('Error fetching admin emails:', error);
      return ['javedahmedshaikh@gmail.com']; // Fallback email
    }
  };

  // Function to create professional email template for demo requests
  const createDemoEmailTemplate = (submissionData: any) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Demo Request</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center;">
          <h1 style="margin: 0; font-size: 28px; font-weight: bold;">🚀 New Demo Request</h1>
          <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">A potential client wants to see your AI automation solutions!</p>
        </div>
        
        <div style="background: #f8fafc; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e2e8f0;">
          <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <h2 style="color: #1e293b; margin-top: 0; font-size: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">👤 Contact Information</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569; width: 30%;">Name:</td>
                <td style="padding: 8px 0; color: #1e293b;">${submissionData.full_name}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Email:</td>
                <td style="padding: 8px 0; color: #1e293b;"><a href="mailto:${submissionData.email_address}" style="color: #3b82f6; text-decoration: none;">${submissionData.email_address}</a></td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Company:</td>
                <td style="padding: 8px 0; color: #1e293b;">${submissionData.company_name}</td>
              </tr>
              ${submissionData.phone_number ? `
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Phone:</td>
                <td style="padding: 8px 0; color: #1e293b;"><a href="tel:${submissionData.phone_number}" style="color: #3b82f6; text-decoration: none;">${submissionData.phone_number}</a></td>
              </tr>
              ` : ''}
              ${submissionData.industry ? `
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Industry:</td>
                <td style="padding: 8px 0; color: #1e293b;"><span style="background: #ddd6fe; color: #5b21b6; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold;">${submissionData.industry.toUpperCase()}</span></td>
              </tr>
              ` : ''}
            </table>
          </div>

          <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <h2 style="color: #1e293b; margin-top: 0; font-size: 20px; border-bottom: 2px solid #8b5cf6; padding-bottom: 10px;">📅 Demo Details</h2>
            ${submissionData.preferred_demo_date ? `
              <p style="margin-bottom: 15px;"><strong style="color: #475569;">Preferred Demo Date:</strong> <span style="background: #fef3c7; color: #92400e; padding: 4px 8px; border-radius: 4px; font-weight: bold;">${new Date(submissionData.preferred_demo_date).toLocaleDateString()}</span></p>
            ` : ''}
            <p style="margin-bottom: 5px;"><strong style="color: #475569;">What they want to automate:</strong></p>
            <div style="background: #f1f5f9; padding: 15px; border-radius: 6px; border-left: 4px solid #3b82f6; font-style: italic; color: #334155;">
              "${submissionData.automation_needs}"
            </div>
          </div>

          <div style="background: white; padding: 25px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <h2 style="color: #1e293b; margin-top: 0; font-size: 20px; border-bottom: 2px solid #10b981; padding-bottom: 10px;">📊 Submission Details</h2>
            <p style="margin: 10px 0; color: #475569;"><strong>Submitted on:</strong> ${new Date(submissionData.submission_date).toLocaleString()}</p>
            <p style="margin: 10px 0; color: #475569;"><strong>Status:</strong> <span style="background: #fef3c7; color: #92400e; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold;">PENDING REVIEW</span></p>
          </div>

          <div style="text-align: center; margin-top: 30px; padding: 20px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); border-radius: 8px;">
            <p style="color: white; margin: 0; font-size: 16px; font-weight: bold;">💡 Ready to showcase your AI automation solutions?</p>
            <p style="color: rgba(255,255,255,0.9); margin: 5px 0 0 0; font-size: 14px;">Contact ${submissionData.full_name} to schedule their personalized demo!</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Function to create professional email template for general inquiries
  const createInquiryEmailTemplate = (submissionData: any) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New General Inquiry</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center;">
          <h1 style="margin: 0; font-size: 28px; font-weight: bold;">💬 New General Inquiry</h1>
          <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Someone is interested in your AI automation services!</p>
        </div>
        
        <div style="background: #f8fafc; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e2e8f0;">
          <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <h2 style="color: #1e293b; margin-top: 0; font-size: 20px; border-bottom: 2px solid #8b5cf6; padding-bottom: 10px;">👤 Contact Information</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569; width: 30%;">Name:</td>
                <td style="padding: 8px 0; color: #1e293b;">${submissionData.full_name}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Email:</td>
                <td style="padding: 8px 0; color: #1e293b;"><a href="mailto:${submissionData.email_address}" style="color: #8b5cf6; text-decoration: none;">${submissionData.email_address}</a></td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Company:</td>
                <td style="padding: 8px 0; color: #1e293b;">${submissionData.company_name}</td>
              </tr>
              ${submissionData.phone_number ? `
              <tr>
                <td style="padding: 8px 0; font-weight: bold; color: #475569;">Phone:</td>
                <td style="padding: 8px 0; color: #1e293b;"><a href="tel:${submissionData.phone_number}" style="color: #8b5cf6; text-decoration: none;">${submissionData.phone_number}</a></td>
              </tr>
              ` : ''}
            </table>
          </div>

          <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <h2 style="color: #1e293b; margin-top: 0; font-size: 20px; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">💭 Their Message</h2>
            <div style="background: #f1f5f9; padding: 20px; border-radius: 6px; border-left: 4px solid #8b5cf6; font-style: italic; color: #334155; font-size: 16px; line-height: 1.6;">
              "${submissionData.inquiry_message}"
            </div>
          </div>

          <div style="background: white; padding: 25px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <h2 style="color: #1e293b; margin-top: 0; font-size: 20px; border-bottom: 2px solid #10b981; padding-bottom: 10px;">📊 Submission Details</h2>
            <p style="margin: 10px 0; color: #475569;"><strong>Submitted on:</strong> ${new Date(submissionData.submission_date).toLocaleString()}</p>
            <p style="margin: 10px 0; color: #475569;"><strong>Status:</strong> <span style="background: #fef3c7; color: #92400e; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold;">PENDING REVIEW</span></p>
          </div>

          <div style="text-align: center; margin-top: 30px; padding: 20px; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); border-radius: 8px;">
            <p style="color: white; margin: 0; font-size: 16px; font-weight: bold;">📞 Time to connect!</p>
            <p style="color: rgba(255,255,255,0.9); margin: 5px 0 0 0; font-size: 14px;">Reach out to ${submissionData.full_name} to address their inquiry!</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Enhanced email notification function
  const sendNotificationEmail = async (submissionData: any) => {
    try {
      // Get admin emails from database
      const adminEmails = await getAdminEmails();
      console.log('Admin emails retrieved:', adminEmails);

      // Prepare email content based on form type
      const isDemo = formType === 'demo';
      const subject = `🚀 New ${isDemo ? 'Demo Request' : 'General Inquiry'} from ${submissionData.full_name} - ${submissionData.company_name}`;
      const htmlContent = isDemo ?
      createDemoEmailTemplate(submissionData) :
      createInquiryEmailTemplate(submissionData);

      // Create plain text version as fallback
      const textContent = `
        New ${isDemo ? 'Demo Request' : 'General Inquiry'} received:
        
        Contact Information:
        - Name: ${submissionData.full_name}
        - Email: ${submissionData.email_address}
        - Company: ${submissionData.company_name}
        - Phone: ${submissionData.phone_number || 'Not provided'}
        ${submissionData.industry ? `- Industry: ${submissionData.industry}` : ''}
        
        ${isDemo ? 'Demo Details:' : 'Message:'}
        ${isDemo ? `- Preferred Date: ${submissionData.preferred_demo_date ? new Date(submissionData.preferred_demo_date).toLocaleDateString() : 'Not specified'}` : ''}
        ${isDemo ? `- What they want to automate: ${submissionData.automation_needs}` : `- Their message: ${submissionData.inquiry_message}`}
        
        Submitted on: ${new Date(submissionData.submission_date).toLocaleString()}
        Status: Pending Review
      `;

      // Send email to all admins
      const { error } = await window.ezsite.apis.sendEmail({
        from: 'AI Automation Co. <noreply@aiautomationco.com>',
        to: adminEmails,
        subject: subject,
        text: textContent,
        html: htmlContent
      });

      if (error) {
        throw new Error(error);
      }

      console.log('Email notification sent successfully to:', adminEmails);
      return true;
    } catch (error) {
      console.error('Error sending email notification:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation
      if (!name || !email || !company || !message) {
        toast({
          title: "Missing Information",
          description: "Please fill out all required fields.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Prepare data for database
      const submissionData = {
        form_type: formType,
        full_name: name,
        email_address: email,
        phone_number: phone || "",
        company_name: company,
        industry: industry || "",
        preferred_demo_date: date ? date.toISOString() : "",
        automation_needs: formType === 'demo' ? message : "",
        inquiry_message: formType === 'contact' ? message : "",
        submission_date: new Date().toISOString(),
        status: "pending"
      };

      console.log('Submitting form data:', submissionData);

      // Save to database
      const { error: dbError } = await window.ezsite.apis.tableCreate(CONTACT_TABLE_ID, submissionData);

      if (dbError) {
        throw new Error(dbError);
      }

      // Send email notification
      try {
        await sendNotificationEmail(submissionData);

        // Success feedback with email confirmation
        toast({
          title: "Form Submitted Successfully! ✉️",
          description: `We've received your ${formType === 'demo' ? 'demo request' : 'inquiry'} and sent a notification to our team. We'll contact you shortly!`
        });
      } catch (emailError) {
        console.error('Email sending failed:', emailError);

        // Partial success - form saved but email failed
        toast({
          title: "Form Submitted",
          description: `Your ${formType === 'demo' ? 'demo request' : 'inquiry'} has been saved. Our team will contact you shortly. (Email notification temporarily unavailable)`,
          variant: "default"
        });
      }

      setFormSubmitted(true);

      // Reset form after successful submission
      setTimeout(() => {
        setName("");
        setEmail("");
        setPhone("");
        setCompany("");
        setIndustry("");
        setMessage("");
        setDate(undefined);
        setFormSubmitted(false);
      }, 5000);

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your form. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout data-id="aug5cnvjh" data-path="src/pages/ContactPage.tsx">
      {/* Header section */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16" data-id="j27llt83n" data-path="src/pages/ContactPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="yvwr1dzmo" data-path="src/pages/ContactPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="9zo96tnc0" data-path="src/pages/ContactPage.tsx">
            <h1 className="text-4xl font-bold text-gray-900 mb-6" data-id="gydyz6v1c" data-path="src/pages/ContactPage.tsx">
              Get in Touch
            </h1>
            <p className="text-lg text-gray-600 mb-8" data-id="8zosy1nts" data-path="src/pages/ContactPage.tsx">
              Our team of AI automation experts is ready to help you transform your business operations.
              Book a demo or send us a general inquiry.
            </p>
          </div>
        </div>
      </div>
      
      {/* Contact form section */}
      <div className="py-16 bg-white" data-id="1kwy1y7dg" data-path="src/pages/ContactPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="xxl76ocoa" data-path="src/pages/ContactPage.tsx">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="6piy7o21g" data-path="src/pages/ContactPage.tsx">
            <div className="lg:col-span-2" data-id="9ps205fqf" data-path="src/pages/ContactPage.tsx">
              <Tabs value={formType} onValueChange={(value) => setFormType(value as "demo" | "contact")} data-id="c7kzsqseq" data-path="src/pages/ContactPage.tsx">
                <TabsList className="grid w-full grid-cols-2 bg-gray-100" data-id="23b0hzh60" data-path="src/pages/ContactPage.tsx">
                  <TabsTrigger
                    value="demo"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="jcb66hubz" data-path="src/pages/ContactPage.tsx">
                    Book a Demo
                  </TabsTrigger>
                  <TabsTrigger
                    value="contact"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-purple-600 data-[state=active]:text-white" data-id="s3wlkxlwg" data-path="src/pages/ContactPage.tsx">
                    General Inquiry
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="demo" className="mt-6" data-id="6lfycpavl" data-path="src/pages/ContactPage.tsx">
                  <div className="bg-white rounded-lg p-6" data-id="o5milwhac" data-path="src/pages/ContactPage.tsx">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="aps6gsv8n" data-path="src/pages/ContactPage.tsx">Schedule a Demo</h2>
                    <p className="text-gray-600 mb-6" data-id="e51mngjkc" data-path="src/pages/ContactPage.tsx">
                      See our AI automation solutions in action with a personalized demo tailored to your business needs.
                    </p>
                    
                    <form onSubmit={handleSubmit} data-id="f08z3dj5p" data-path="src/pages/ContactPage.tsx">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="9y88afs21" data-path="src/pages/ContactPage.tsx">
                        <div data-id="yy1wwcbt8" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="hvuyarui6" data-path="src/pages/ContactPage.tsx">
                            Full Name *
                          </label>
                          <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="John Smith"
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={isSubmitting} data-id="k622wmdcd" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="v17haf3wf" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="l4c8rjwsf" data-path="src/pages/ContactPage.tsx">
                            Email Address *
                          </label>
                          <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="john@company.com"
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={isSubmitting} data-id="7k1lydfs0" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="mnuey1m83" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="gxtbrnxtv" data-path="src/pages/ContactPage.tsx">
                            Phone Number
                          </label>
                          <Input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+1 (555) 000-0000"
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={isSubmitting} data-id="bn3zxojor" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="wl9qd2ldv" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1" data-id="ihslnip92" data-path="src/pages/ContactPage.tsx">
                            Company Name *
                          </label>
                          <Input
                            id="company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            placeholder="Acme Inc."
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={isSubmitting} data-id="89clx3orm" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="6ntoobyxz" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1" data-id="1thf50gvj" data-path="src/pages/ContactPage.tsx">
                            Industry
                          </label>
                          <Select value={industry} onValueChange={setIndustry} disabled={isSubmitting} data-id="rt9wrt97y" data-path="src/pages/ContactPage.tsx">
                            <SelectTrigger id="industry" className="light-input bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="ypp5rqdqa" data-path="src/pages/ContactPage.tsx">
                              <SelectValue placeholder="Select an industry" data-id="pvzy8jt3m" data-path="src/pages/ContactPage.tsx" />
                            </SelectTrigger>
                            <SelectContent className="bg-white" data-id="y91eikqen" data-path="src/pages/ContactPage.tsx">
                              <SelectItem value="healthcare" data-id="5nzinfn8h" data-path="src/pages/ContactPage.tsx">Healthcare</SelectItem>
                              <SelectItem value="recruitment" data-id="f7qzekjjh" data-path="src/pages/ContactPage.tsx">Recruitment</SelectItem>
                              <SelectItem value="real-estate" data-id="yim0p5z74" data-path="src/pages/ContactPage.tsx">Real Estate</SelectItem>
                              <SelectItem value="hospitality" data-id="fr3fx6wxy" data-path="src/pages/ContactPage.tsx">Hospitality</SelectItem>
                              <SelectItem value="automotive" data-id="lrl50lbef" data-path="src/pages/ContactPage.tsx">Automotive</SelectItem>
                              <SelectItem value="marketing" data-id="fgljp9797" data-path="src/pages/ContactPage.tsx">Marketing</SelectItem>
                              <SelectItem value="agency" data-id="tu3lvbqlv" data-path="src/pages/ContactPage.tsx">Agency</SelectItem>
                              <SelectItem value="other" data-id="40vgp1fhf" data-path="src/pages/ContactPage.tsx">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div data-id="wgqom9kb4" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1" data-id="cobijh4vs" data-path="src/pages/ContactPage.tsx">
                            Preferred Demo Date
                          </label>
                          <Popover data-id="qfow4bdrd" data-path="src/pages/ContactPage.tsx">
                            <PopoverTrigger asChild data-id="hsu51yjhh" data-path="src/pages/ContactPage.tsx">
                              <Button
                                variant="outline"
                                className="light-input w-full justify-start text-left font-normal bg-white text-gray-900 border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                disabled={isSubmitting} data-id="73y6isvz7" data-path="src/pages/ContactPage.tsx">
                                <CalendarIcon className="mr-2 h-4 w-4" data-id="gefkwa4db" data-path="src/pages/ContactPage.tsx" />
                                {date ? format(date, "PPP") : <span data-id="tsvb5su3v" data-path="src/pages/ContactPage.tsx">Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white" data-id="3l4zpqa8k" data-path="src/pages/ContactPage.tsx">
                              <Calendar
                                mode="single"
                                selected={date}
                                onSelect={setDate}
                                initialFocus data-id="sf33oa4ae" data-path="src/pages/ContactPage.tsx" />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      
                      <div className="mb-6" data-id="g4g76pbca" data-path="src/pages/ContactPage.tsx">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="ez78j2hp5" data-path="src/pages/ContactPage.tsx">
                          What are you looking to automate? *
                        </label>
                        <Textarea
                          id="message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Tell us about your business needs and what you'd like to see in the demo..."
                          className="light-input h-32 bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                          disabled={isSubmitting} data-id="tyg05ipw2" data-path="src/pages/ContactPage.tsx" />
                      </div>
                      
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        disabled={formSubmitted || isSubmitting} data-id="b3wny07jt" data-path="src/pages/ContactPage.tsx">
                        {isSubmitting ?
                        <span className="flex items-center" data-id="jcfgls8rf" data-path="src/pages/ContactPage.tsx">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="wfx40aa41" data-path="src/pages/ContactPage.tsx" />
                            Submitting...
                          </span> :
                        formSubmitted ?
                        <span className="flex items-center" data-id="qlomu6lq9" data-path="src/pages/ContactPage.tsx">
                            <Check className="mr-2 h-4 w-4" data-id="u2httpgbu" data-path="src/pages/ContactPage.tsx" /> Demo Requested
                          </span> :

                        "Book My Demo"
                        }
                      </Button>
                    </form>
                  </div>
                </TabsContent>
                
                <TabsContent value="contact" className="mt-6" data-id="uyi9nceyc" data-path="src/pages/ContactPage.tsx">
                  <div className="bg-white rounded-lg p-6" data-id="uxohnu7pe" data-path="src/pages/ContactPage.tsx">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="m5xtow4mh" data-path="src/pages/ContactPage.tsx">General Inquiry</h2>
                    <p className="text-gray-600 mb-6" data-id="tg3dvy9tj" data-path="src/pages/ContactPage.tsx">
                      Have a question or want to learn more about our services? Send us a message and we'll get back to you.
                    </p>
                    
                    <form onSubmit={handleSubmit} data-id="rd7o83xt3" data-path="src/pages/ContactPage.tsx">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="f52mucgh6" data-path="src/pages/ContactPage.tsx">
                        <div data-id="txbc8nho5" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="gvu0fyvbp" data-path="src/pages/ContactPage.tsx">
                            Full Name *
                          </label>
                          <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="John Smith"
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={isSubmitting} data-id="op3x8l6ox" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="3hh66b9us" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="8oc299ra1" data-path="src/pages/ContactPage.tsx">
                            Email Address *
                          </label>
                          <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="john@company.com"
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={isSubmitting} data-id="g32xbsjsg" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="lwqlvyxlb" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="a5coddty1" data-path="src/pages/ContactPage.tsx">
                            Phone Number
                          </label>
                          <Input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+1 (555) 000-0000"
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={isSubmitting} data-id="u17snedf7" data-path="src/pages/ContactPage.tsx" />
                        </div>
                        <div data-id="zxzgj4ia3" data-path="src/pages/ContactPage.tsx">
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1" data-id="5w7p79xl6" data-path="src/pages/ContactPage.tsx">
                            Company Name *
                          </label>
                          <Input
                            id="company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            placeholder="Acme Inc."
                            className="light-input bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            disabled={isSubmitting} data-id="6ljg0hc9x" data-path="src/pages/ContactPage.tsx" />
                        </div>
                      </div>
                      
                      <div className="mb-6" data-id="ixvxihdpi" data-path="src/pages/ContactPage.tsx">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="ugy3h6qoc" data-path="src/pages/ContactPage.tsx">
                          Message *
                        </label>
                        <Textarea
                          id="message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="How can we help you?"
                          className="light-input h-32 bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                          disabled={isSubmitting} data-id="sgoyqmfof" data-path="src/pages/ContactPage.tsx" />
                      </div>
                      
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        disabled={formSubmitted || isSubmitting} data-id="3apfbjq5r" data-path="src/pages/ContactPage.tsx">
                        {isSubmitting ?
                        <span className="flex items-center" data-id="292u2po55" data-path="src/pages/ContactPage.tsx">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="12166zfnz" data-path="src/pages/ContactPage.tsx" />
                            Submitting...
                          </span> :
                        formSubmitted ?
                        <span className="flex items-center" data-id="v9gyhnwpn" data-path="src/pages/ContactPage.tsx">
                            <Check className="mr-2 h-4 w-4" data-id="ba9aqw5me" data-path="src/pages/ContactPage.tsx" /> Message Sent
                          </span> :

                        "Send Message"
                        }
                      </Button>
                    </form>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            <div data-id="zs3jxrqf1" data-path="src/pages/ContactPage.tsx">
              <div className="bg-gray-50 rounded-lg p-6" data-id="1rgrhvrcm" data-path="src/pages/ContactPage.tsx">
                <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="lscyswiyk" data-path="src/pages/ContactPage.tsx">Contact Information</h2>
                
                <div className="space-y-6" data-id="69jt1eq76" data-path="src/pages/ContactPage.tsx">
                  <div className="flex" data-id="dnjoocgr7" data-path="src/pages/ContactPage.tsx">
                    <Mail className="h-6 w-6 text-blue-600 mr-4" data-id="r6orhhnfo" data-path="src/pages/ContactPage.tsx" />
                    <div data-id="pdbynqp35" data-path="src/pages/ContactPage.tsx">
                      <h3 className="text-sm font-medium text-gray-900" data-id="ku43fs6zu" data-path="src/pages/ContactPage.tsx">Email</h3>
                      <p className="text-gray-600" data-id="odnalb2pw" data-path="src/pages/ContactPage.tsx">info@botlabhq.com</p>
                    </div>
                  </div>
                  
                  <div className="flex" data-id="19oflvlmk" data-path="src/pages/ContactPage.tsx">
                    <MapPin className="h-6 w-6 text-blue-600 mr-4" data-id="vsz3wblv3" data-path="src/pages/ContactPage.tsx" />
                    <div data-id="8od75v1yc" data-path="src/pages/ContactPage.tsx">
                      <h3 className="text-sm font-medium text-gray-900" data-id="x6ooch68g" data-path="src/pages/ContactPage.tsx">Office</h3>
                      <p className="text-gray-600" data-id="cm42avn3t" data-path="src/pages/ContactPage.tsx">
                        123 Innovation Drive<br data-id="hb5atusw0" data-path="src/pages/ContactPage.tsx" />
                        Suite 456<br data-id="19ntkuivs" data-path="src/pages/ContactPage.tsx" />
                        San Francisco, CA 94103
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8" data-id="ni6d1p33s" data-path="src/pages/ContactPage.tsx">
                  <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="6t7orc1cz" data-path="src/pages/ContactPage.tsx">Hours</h3>
                  <p className="text-gray-600" data-id="9kslree1k" data-path="src/pages/ContactPage.tsx">
                    Monday - Friday: 9am - 6pm PST<br data-id="54uu9uhwn" data-path="src/pages/ContactPage.tsx" />
                    Saturday - Sunday: Closed
                  </p>
                </div>
              </div>
              
              <div className="mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6" data-id="rmmz0pezx" data-path="src/pages/ContactPage.tsx">
                <h3 className="text-lg font-medium mb-2" data-id="lvx47ysfc" data-path="src/pages/ContactPage.tsx">Need immediate assistance?</h3>
                <p className="mb-4" data-id="1trcsda7k" data-path="src/pages/ContactPage.tsx">Our AI chatbot is available 24/7 to answer your questions.</p>
                <Button className="bg-white text-blue-600 hover:bg-blue-50 w-full" data-id="d5sbi7k1n" data-path="src/pages/ContactPage.tsx">
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>);

}